/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : registry.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import { EDIT_TYPES } from "../../../config/appConstants";

/* -------------------- HELPERS -------------------- */

/**
 * Block rules:
 * 1. SNOW edit_type → always blocked
 * 2. If value existed FROM DB on mount → blocked
 * 3. Otherwise editable forever
 */
export const isFieldBlocked = ({ el, initialValue }) => {
  if (!el) return true;

  const editType = el.edit_type ?? el?.properties?.edit_type;

  // Rule 1: SNOW
  if (editType === EDIT_TYPES.SNOW) return true;

  // Rule 2: value existed from DB
  if (
    initialValue !== null &&
    initialValue !== undefined &&
    String(initialValue).length > 0
  ) {
    return true;
  }

  return false;
};
const DELOITTE_GREEN = "#26890D";
const DeloitteRadio = ({ checked }) => (
  <div
    style={{
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: `2px solid black`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 6,
      boxSizing: "border-box",
    }}
  >
    {checked && (
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: DELOITTE_GREEN,
        }}
      />
    )}
  </div>
);

const buildTextStyle = (el) => {
  const p = el.properties || {};

  return {
    fontSize: p.fontSize ?? 14,
    fontWeight: p.bold ? 700 : 400,
    fontStyle: p.italic ? "italic" : "normal",
    textDecoration:
      [p.underline ? "underline" : "", p.strike ? "line-through" : ""]
        .join(" ")
        .trim() || undefined,
    textAlign: p.alignment ?? "left",
    fontFamily: p.fontFamily ?? "inherit",
    lineHeight: p.lineHeight ? String(p.lineHeight) : undefined,
    letterSpacing: p.letterSpacing ? `${p.letterSpacing}px` : undefined,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  };
};

const TABLE_HEADER_HEIGHT = 34;
const MIN_ROW_HEIGHT = 28;

const getRowHeightFromTable = (el) => {
  const totalHeight = el.height || 0;
  const rows = el.properties?.rows || 1;

  const bodyHeight = Math.max(
    totalHeight - TABLE_HEADER_HEIGHT,
    MIN_ROW_HEIGHT * rows,
  );

  return Math.floor(bodyHeight / rows);
};

const TABLE_BASE_STYLE = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
  background: "#fff",
};

const HEADER_CELL_STYLE = {
  border: "1px solid #cbd5e1",
  background: "#f1f5f9",
  padding: 0,
};

const BODY_CELL_STYLE = {
  border: "1px solid #cbd5e1",
  padding: 0,
};

const CELL_CONTENT_STYLE = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  padding: "6px",
  boxSizing: "border-box",
  wordBreak: "break-word",
};

const withDeloitteBorder = (baseStyle) => ({
  ...baseStyle,
  width: "100%",
  height: "100%", // 🔥 THIS IS THE KEY
  border: "1px solid #000",
  borderRadius: "2px",
  padding: "6px 8px",
  boxSizing: "border-box",
  // vertical alignment
});

const DeloitteCheckbox = ({ checked }) => (
  <div
    style={{
      width: 14,
      height: 14,
      border: `2px solid black`,
      backgroundColor: checked ? DELOITTE_GREEN : "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 6,
      boxSizing: "border-box",
    }}
  >
    {checked && (
      <div
        style={{
          width: 6,
          height: 10,
          border: "solid white",
          borderWidth: "0 2px 2px 0",
          transform: "rotate(45deg)",
          marginBottom: 2,
        }}
      />
    )}
  </div>
);

/* -------------------- REGISTRY -------------------- */

export const ComponentRegistry = {
  /* ---------- TEXT / HEADING ---------- */

  heading: {
    canvas: (el) => {
      console.log(withDeloitteBorder(buildTextStyle(el)), "style");
      return (
        <div style={withDeloitteBorder(buildTextStyle(el))}>
          {el.properties.text}
        </div>
      );
    },
    render: (el) => <div style={buildTextStyle(el)}>{el.properties.text}</div>,
    preview: (el) => <div style={buildTextStyle(el)}>{el.properties.text}</div>,
    defaultValue: () => null,
    updateJson: (el) => el.properties.text,
  },

  text: {
    canvas: (el) => (
      <div style={withDeloitteBorder(buildTextStyle(el))}>
        {el.properties.text}
      </div>
    ),
    render: (el) => <div style={buildTextStyle(el)}>{el.properties.text}</div>,
    preview: (el) => <div style={buildTextStyle(el)}>{el.properties.text}</div>,
    defaultValue: () => null,
    updateJson: (el) => el.properties.text,
  },

  /* ---------- INPUT ---------- */

  input: {
    canvas: (el) => (
      <div style={withDeloitteBorder(buildTextStyle(el))}>
        {el.properties.placeholder || "Input Field"}
      </div>
    ),

    render: (el, value, setValue, initialValue) => {
      console.log(initialValue, "initialValue");
      const isBlocked = isFieldBlocked({
        el,
        initialValue,
      });
      console.log(isBlocked, "isBlocked");
      console.log(el, "el");
      return (
        <input
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value)}
          placeholder={el.properties.placeholder}
          disabled={isBlocked}
          style={{
            ...withDeloitteBorder(buildTextStyle(el)),
            backgroundColor: isBlocked ? "#f3f3f3" : "white",
            cursor: isBlocked ? "not-allowed" : "text",
          }}
        />
      );
    },

    preview: (el) => (
      <div style={buildTextStyle(el)}>
        {el.properties.placeholder} :{el.properties.value || ""}
      </div>
    ),

    defaultValue: (el) => el.properties.default_value ?? "",
    updateJson: (_, val) => val,
  },

  /* ---------- SIGNATURE ---------- */

  signature: {
    canvas: (el) => (
      <div
        style={{
          ...withDeloitteBorder(buildTextStyle(el)),

          borderBottom: `2px solid ${el.properties.borderColor}`,
          height: "100%",
        }}
      >
        {el.properties.label}
      </div>
    ),

    render: (el, value, setValue, initialValue) => {
      const style = buildTextStyle(el);
      const isBlocked = isFieldBlocked({
        el,
        initialValue,
      });
      return (
        <input
          style={{
            ...style,
            ...withDeloitteBorder(buildTextStyle(el)),

            width: "100%",
            border: "none",
            borderBottom: "1px solid #aaa",
          }}
          disabled={isBlocked}
          value={value ?? ""}
          placeholder={el.properties.label}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    },

    preview: (el) => {
      const style = buildTextStyle(el);
      return (
        <div
          style={{
            borderBottom: `2px solid ${el.properties.borderColor}`,
            ...style,
          }}
        >
          {el.properties.value}
        </div>
      );
    },

    defaultValue: () => "",
    updateJson: (_, val) => val,
  },

  /* ---------- DATE ---------- */

  date: {
    // Designer canvas (shows sample date)
    canvas: () => (
      <div style={{ fontSize: 14 }}>{new Date().toLocaleDateString()}</div>
    ),

    // Form render (shows stored value)
    render: (_el, value) => (
      <div style={{ fontSize: 14 }}>
        {value ? new Date(value).toLocaleDateString() : "-"}
      </div>
    ),

    // Preview mode (same as render, read-only)
    preview: (_el, value) => (
      <div style={{ fontSize: 14 }}>
        {value
          ? new Date(value).toLocaleDateString()
          : new Date().toLocaleDateString()}
      </div>
    ),

    // Store date ONCE when form loads
    defaultValue: () => new Date().toISOString(),

    // Save stored date value
    updateJson: (_el, value) => value,
  },

  /* ---------- DIVIDER ---------- */

  divider: {
    canvas: (el) => (
      <div
        style={{
          width: "100%",
          height: el.properties.thickness,
          background: el.properties.color,
        }}
      />
    ),
    render: (el) => {
      const style = buildTextStyle(el);
      return (
        <div
          style={{
            ...style,
            width: "100%",
            height: el.properties.thickness,
            background: el.properties.color,
          }}
        />
      );
    },
    preview: (el) => (
      <div
        style={{
          width: "100%",
          height: el.properties.thickness,
          background: el.properties.color,
        }}
      />
    ),
    defaultValue: () => null,
    updateJson: () => null,
  },

  /* ---------- IMAGE ---------- */

  image: {
    canvas: (el, update, isEditing, setIsEditing) => (
      <div
        style={{
          width: "100%",
          height: "100%",
          border: "1px dashed #94a3b8",
          borderRadius: el.properties.borderRadius ?? 0,
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      >
        {el.properties.src ? (
          <img
            src={el.properties.src}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <div style={{ opacity: 0.6 }}>Double-click to upload</div>
        )}

        {isEditing && (
          <input
            type="file"
            accept="image/*"
            style={{ position: "absolute", inset: 0, opacity: 0 }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              const reader = new FileReader();
              reader.onload = () => {
                update({
                  properties: { ...el.properties, src: reader.result },
                });
                setIsEditing(false);
              };
              reader.readAsDataURL(file);
            }}
          />
        )}
      </div>
    ),

    render: (el) => (
      <img
        src={el.properties.src}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    ),

    preview: (el) => (
      <img
        src={el.properties.src}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    ),

    defaultValue: () => null,
    updateJson: (el) => ({
      src: el.properties.src ?? null,
      borderRadius: el.properties.borderRadius ?? 0,
    }),
  },

  /* ---------- TABLE ---------- */

  table: {
    canvas: (el, update, isEditing, setIsEditing) => {
      const columns = el.properties.columns ?? [];
      const rows = el.properties.rows ?? 1;
      const rowHeight = getRowHeightFromTable(el);

      const fixedValues = Array.isArray(el.properties.value)
        ? el.properties.value
        : [];

      return (
        <table style={{ ...buildTextStyle(el), ...TABLE_BASE_STYLE }}>
          <thead>
            <tr style={{ height: TABLE_HEADER_HEIGHT }}>
              {columns.map((col) => (
                <th key={col.key} style={HEADER_CELL_STYLE}>
                  <div style={{ ...CELL_CONTENT_STYLE, fontWeight: 600 }}>
                    {col.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: rows }).map((_, rIndex) => (
              <tr key={rIndex} style={{ height: rowHeight }}>
                {columns.map((col) => {
                  const isFixed = col.type === "fixed";
                  const cellValue = fixedValues?.[rIndex]?.[col.key] ?? "";

                  return (
                    <td key={col.key} style={BODY_CELL_STYLE}>
                      <div style={CELL_CONTENT_STYLE}>
                        {isFixed ? (
                          <input
                            value={cellValue}
                            onChange={(e) => {
                              const nextValue = e.target.value;

                              const updated = [...fixedValues];
                              updated[rIndex] = {
                                ...(updated[rIndex] ?? {}),
                                [col.key]: nextValue,
                              };

                              update({
                                ...el,
                                properties: {
                                  ...el.properties,
                                  value: updated,
                                },
                              });
                            }}
                            /*Tell editor we are editing */
                            onFocus={() => setIsEditing?.(true)}
                            onBlur={() => setIsEditing?.(false)}
                            /* Prevent editor selection hijack */
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontSize: 12,
                            }}
                          />
                        ) : (
                          <span>{cellValue || "\u00A0"}</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    },

    // Render = form mode, allow filling table
    render: (el, value, setValue, initialValue, isPdfMode) => {
      const columns = el.properties.columns ?? [];
      const rows = el.properties.rows ?? 1;
      const style = buildTextStyle(el);
      const isPdfModes = isPdfMode ?? false;
      const rowHeight = getRowHeightFromTable(el);

      console.log(initialValue, "initialValue", isPdfMode);
      const v =
        value ||
        new Array(rows)
          .fill(null)
          .map(() => ({}));

      const isTableFilledFromInitialValue = (initialValue, rows, columns) => {
        if (!Array.isArray(initialValue)) return false;

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < columns.length; c++) {
            const key = columns[c].key;
            const cellValue = initialValue[r]?.[key];

            if (
              cellValue === undefined ||
              cellValue === null ||
              String(cellValue).trim() === ""
            ) {
              return false;
            }
          }
        }
        return true;
      };

      const isBlocked =
        isFieldBlocked({ el, initialValue }) &&
        isTableFilledFromInitialValue(initialValue, rows, columns);

      //row height ONLY for rendering (no logic change)

      return (
        <table
          style={{
            ...buildTextStyle(el),
            ...TABLE_BASE_STYLE,
            backgroundColor: isBlocked ? "#f8f8f8" : "#fff",
            opacity: isBlocked ? 0.9 : 1,
          }}
        >
          <thead>
            <tr style={{ height: TABLE_HEADER_HEIGHT }}>
              {columns.map((col, i) => (
                <th key={i} style={HEADER_CELL_STYLE}>
                  <div style={{ ...CELL_CONTENT_STYLE, fontWeight: 600 }}>
                    {col.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {v.map((row, rIndex) => (
              <tr key={rIndex} style={{ height: rowHeight }}>
                {columns.map((col, cIndex) => {
                  const isFixedColumn = col.type === "fixed";
                  const cellValue = row[col.key] || "";

                  const isReadOnly = isPdfModes || isBlocked || isFixedColumn;

                  return (
                    <td key={cIndex} style={BODY_CELL_STYLE}>
                      {isReadOnly ? (
                        <div style={CELL_CONTENT_STYLE}>
                          {cellValue || "\u00A0"}
                        </div>
                      ) : (
                        <div style={CELL_CONTENT_STYLE}>
                          <input
                            value={cellValue}
                            disabled={isBlocked}
                            onChange={(e) => {
                              if (isBlocked) return;
                              const updated = [...v];
                              updated[rIndex] = {
                                ...updated[rIndex],
                                [col.key]: e.target.value,
                              };
                              setValue(updated);
                            }}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontSize: 12,
                            }}
                          />
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    },

    preview: (el, value) => {
      const columns = el.properties.columns ?? [];
      const rows = el.properties.rows ?? 1;
      const data = el.properties.value || [];
      const rowHeight = getRowHeightFromTable(el);
      const style = buildTextStyle(el);

      return (
        <table style={{ ...buildTextStyle(el), ...TABLE_BASE_STYLE }}>
          <thead>
            <tr style={{ height: TABLE_HEADER_HEIGHT }}>
              {columns.map((col, i) => (
                <th key={i} style={HEADER_CELL_STYLE}>
                  <div style={{ ...CELL_CONTENT_STYLE, fontWeight: 600 }}>
                    {col.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {[...Array(rows)].map((_, r) => (
              <tr key={r} style={{ height: rowHeight }}>
                {columns.map((col, c) => (
                  <td key={c} style={BODY_CELL_STYLE}>
                    <div style={CELL_CONTENT_STYLE}>
                      {data?.[r]?.[col.key] || ""}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    },

    defaultValue: () => null,

    updateJson: (el, value) => ({
      rows: el.properties.rows,
      columns: el.properties.columns,
      values: value || [],
    }),
  },

  /* ---------- RADIO ---------- */

  radio: {
    canvas: (el) => (
      <div style={{ fontSize: 14 }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>
          {el.properties.label}
        </div>

        {(el.properties.options ?? []).map((opt) => (
          <div key={opt.key} style={{ display: "flex", alignItems: "center" }}>
            <DeloitteRadio checked={false} />
            {opt.label}
          </div>
        ))}
      </div>
    ),

    render: (el, value, setValue, initialValue) => {
      const isBlocked = isFieldBlocked({
        el,
        initialValue,
      });

      return (
        <div
          style={{
            fontSize: 14,
            opacity: isBlocked ? 0.7 : 1,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>
            {el.properties.label}
          </div>

          {(el.properties.options ?? []).map((opt) => {
            const checked = value === opt.label;

            return (
              <div
                key={opt.key}
                onClick={() => {
                  if (isBlocked) return; // ✅ HARD BLOCK
                  setValue(opt.label);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                  cursor: isBlocked ? "not-allowed" : "pointer",
                }}
              >
                <DeloitteRadio checked={checked} />
                {opt.label}
              </div>
            );
          })}
        </div>
      );
    },

    preview: (el) => {
      const selected = el.properties.value;

      return (
        <div style={{ fontSize: 14 }}>
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>
            {el.properties.label}
          </div>

          {(el.properties.options ?? []).map((opt) => (
            <div
              key={opt.key}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 4,
                opacity: selected === opt.label ? 1 : 0.5,
              }}
            >
              <DeloitteRadio checked={selected === opt.label} />
              {opt.label}
            </div>
          ))}
        </div>
      );
    },

    defaultValue: () => "",
    updateJson: (_el, val) => val,
  },

  checkbox: {
    canvas: (el) => (
      <div style={{ fontSize: 14 }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>
          {el.properties.label}
        </div>

        {(el.properties.options ?? []).map((opt) => (
          <div key={opt.key} style={{ display: "flex", alignItems: "center" }}>
            <DeloitteCheckbox checked={false} />
            {opt.label}
          </div>
        ))}
      </div>
    ),

    render: (el, value, setValue, initialValue) => {
      const selected = value ?? [];

      const isBlocked = isFieldBlocked({
        el,
        initialValue,
      });

      const toggle = (label) => {
        if (isBlocked) return; // ✅ HARD BLOCK

        if (selected.includes(label)) {
          setValue(selected.filter((v) => v !== label));
        } else {
          setValue([...selected, label]);
        }
      };

      return (
        <div
          style={{
            fontSize: 14,
            opacity: isBlocked ? 0.7 : 1,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>
            {el.properties.label}
          </div>

          {(el.properties.options ?? []).map((opt) => {
            const checked = selected.includes(opt.label);

            return (
              <div
                key={opt.key}
                onClick={() => toggle(opt.label)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                  cursor: isBlocked ? "not-allowed" : "pointer",
                }}
              >
                <DeloitteCheckbox checked={checked} />
                {opt.label}
              </div>
            );
          })}
        </div>
      );
    },

    preview: (el) => {
      const selected = el.properties.value ?? [];

      return (
        <div style={{ fontSize: 14 }}>
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>
            {el.properties.label}
          </div>

          {(el.properties.options ?? []).map((opt) => (
            <div
              key={opt.key}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 4,
                opacity: selected.includes(opt.label) ? 1 : 0.5,
              }}
            >
              <DeloitteCheckbox checked={selected.includes(opt.label)} />
              {opt.label}
            </div>
          ))}
        </div>
      );
    },

    defaultValue: () => [],
    updateJson: (_el, val) => val,
  },
};
